<template>
  <div 
    class="hero-container" 
    ref="containerRef"
    @mousemove="handleMouseMove"
    @click="handleRipple"
  >
    <!-- Parallax 3D Layers -->
    <div class="parallax-wrapper" :style="parallaxStyle">
      <!-- Layer 0: Background image -->
      <div class="parallax-layer" data-depth="0.02">
        <img 
          src="/portada.png" 
          alt="Vision House" 
          class="hero-background"
        />
      </div>
      
      <!-- Layer 1: Atmospheric gradients -->
      <div class="parallax-layer" data-depth="0.04">
        <div class="atmospheric-overlay"></div>
      </div>
      
      <!-- Layer 2: Geometric shapes -->
      <div class="parallax-layer" data-depth="0.06">
        <div class="geometric-shapes">
          <div class="shape circle-gold" v-for="i in 5" :key="'gold-'+i"></div>
          <div class="shape circle-purple" v-for="i in 3" :key="'purple-'+i"></div>
        </div>
      </div>
      
      <!-- Layer 3: Content -->
      <div class="parallax-layer content-layer" data-depth="0.08">
        <div class="hero-content">
          <div class="hero-badge">
            <FloatingLogo size="sm" color="gold" :show-label="false" />
            <span>Ministerio Cristiano de Misiones</span>
          </div>
          <h1 class="hero-title">
            <span class="word" v-for="(word, i) in titleWords" :key="i" :style="{ animationDelay: (0.5 + i * 0.15) + 's' }">
              {{ word }}
            </span>
          </h1>
          <p class="hero-subtitle">
            En Vision House, creemos que cada niño y joven tiene un propósito eterno.
            Somos el puente entre su potencial y su misión.
          </p>
          <div class="btn-group">
            <ion-button class="vh-primary" size="large" @click="scrollTo('voluntariado')">
              <i class="fas fa-heart" style="margin-right:8px"></i> ¡Quiero Sumarse!
            </ion-button>
            <ion-button class="vh-outline" size="large" @click="scrollTo('nosotros')">
              <i class="fas fa-play" style="margin-right:8px"></i> Conócenos
            </ion-button>
          </div>
        </div>
      </div>
      
      <!-- Layer 4: V-Kids Logo -->
      <div class="parallax-layer" data-depth="0.10">
        <div class="v-kids-position">
          <FloatingLogo size="md" color="gold" show-label />
        </div>
      </div>
    </div>
    
    <!-- Layer 5: Canvas Particles -->
    <div class="parallax-layer" data-depth="0.14" style="z-index: 50;">
      <canvas ref="particlesCanvas" class="particles-canvas"></canvas>
    </div>
    
    <!-- Ripple container -->
    <div class="ripple-container" ref="rippleContainer"></div>
    
    <!-- Scroll indicator -->
    <div class="scroll-indicator" @click="scrollTo('nosotros')">
      <i class="fas fa-chevron-down fa-2x"></i>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { IonButton } from '@ionic/vue'
import FloatingLogo from '@/components/ui/FloatingLogo.vue'

const containerRef = ref<HTMLElement | null>(null)
const rippleContainer = ref<HTMLElement | null>(null)
const particlesCanvas = ref<HTMLCanvasElement | null>(null)

const mouseX = ref(0)
const mouseY = ref(0)
const targetX = ref(0)
const targetY = ref(0)
const currentX = ref(0)
const currentY = ref(0)

const titleWords = ['Equipando', 'Generaciones,', 'Transformando', 'Naciones']

const parallaxStyle = computed(() => ({
  transform: `perspective(1000px) rotateX(${currentY.value * 0.5}deg) rotateY(${currentX.value * 0.5}deg)`
}))

const lerp = (start: number, end: number, factor: number) => start + (end - start) * factor

const handleMouseMove = (event: MouseEvent) => {
  if (!containerRef.value) return
  const rect = containerRef.value.getBoundingClientRect()
  mouseX.value = (event.clientX - rect.left) / rect.width - 0.5
  mouseY.value = (event.clientY - rect.top) / rect.height - 0.5
}

const handleRipple = (event: MouseEvent) => {
  const target = event.currentTarget as HTMLElement
  const rect = target.getBoundingClientRect()
  const x = event.clientX - rect.left
  const y = event.clientY - rect.top
  
  const ripple = document.createElement('div')
  ripple.className = 'ripple-effect'
  ripple.style.left = x + 'px'
  ripple.style.top = y + 'px'
  
  if (rippleContainer.value) {
    rippleContainer.value.appendChild(ripple)
    setTimeout(() => ripple.remove(), 600)
  }
}

const scrollTo = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

let animationId: number | null = null

const initParticles = () => {
  if (!particlesCanvas.value) return
  const canvas = particlesCanvas.value
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  
  canvas.width = window.innerWidth
  canvas.height = window.innerHeight
  
  const particles: Array<{
    x: number; y: number; size: number; speed: number; opacity: number; phase: number
  }> = []
  
  for (let i = 0; i < 80; i++) {
    particles.push({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      size: Math.random() * 3 + 1,
      speed: Math.random() * 0.5 + 0.2,
      opacity: Math.random() * 0.5 + 0.3,
      phase: Math.random() * Math.PI * 2
    })
  }
  
  const animate = () => {
    if (!ctx || !particlesCanvas.value) return
    
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    
    particles.forEach(p => {
      p.y -= p.speed
      if (p.y < -10) {
        p.y = canvas.height + 10
        p.x = Math.random() * canvas.width
      }
      
      const pulseOpacity = p.opacity * (0.7 + 0.3 * Math.sin(p.phase))
      p.phase += 0.02
      
      ctx.beginPath()
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2)
      ctx.fillStyle = `hsla(45, 100%, 60%, ${pulseOpacity})`
      ctx.shadowColor = 'hsla(45, 100%, 60%, 0.8)'
      ctx.shadowBlur = 8
      ctx.fill()
      ctx.shadowBlur = 0
    })
    
    animationId = requestAnimationFrame(animate)
  }
  
  animate()
}

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

onMounted(() => {
  if (!prefersReducedMotion) {
    const animateParallax = () => {
      targetX.value = mouseX.value * 20
      targetY.value = mouseY.value * 20
      currentX.value = lerp(currentX.value, targetX.value, 0.08)
      currentY.value = lerp(currentY.value, targetY.value, 0.08)
      requestAnimationFrame(animateParallax)
    }
    animateParallax()
    initParticles()
  }
})

onUnmounted(() => {
  if (animationId) cancelAnimationFrame(animationId)
})
</script>

<style scoped>
.hero-container {
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background: linear-gradient(135deg, var(--vh-azul-profundo) 0%, var(--vh-azul-oscuro) 100%);
  cursor: pointer;
}

.parallax-wrapper {
  position: absolute;
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
  transition: transform 0.1s ease-out;
}

.parallax-layer {
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-background {
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.25;
}

.atmospheric-overlay {
  position: absolute;
  width: 100%;
  height: 100%;
  background: 
    radial-gradient(ellipse at 30% 20%, rgba(251, 166, 54, 0.15) 0%, transparent 50%),
    radial-gradient(ellipse at 70% 80%, rgba(142, 68, 173, 0.12) 0%, transparent 50%),
    radial-gradient(ellipse at 50% 50%, rgba(26, 75, 140, 0.3) 0%, transparent 70%);
  background-color: rgba(26, 75, 140, 0.4);
}

.geometric-shapes {
  position: absolute;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.shape {
  position: absolute;
  border-radius: 50%;
  opacity: 0.15;
  animation: floatShape 8s ease-in-out infinite;
}

.circle-gold {
  width: 80px;
  height: 80px;
  background: radial-gradient(circle, var(--hero-gold), transparent 70%);
}

.circle-gold:nth-child(1) { top: 15%; left: 10%; animation-delay: 0s; }
.circle-gold:nth-child(2) { top: 25%; right: 15%; animation-delay: 1s; width: 60px; height: 60px; }
.circle-gold:nth-child(3) { bottom: 30%; left: 20%; animation-delay: 2s; width: 100px; height: 100px; }
.circle-gold:nth-child(4) { top: 40%; right: 25%; animation-delay: 3s; width: 50px; height: 50px; }
.circle-gold:nth-child(5) { bottom: 20%; right: 30%; animation-delay: 4s; width: 70px; height: 70px; }

.circle-purple {
  width: 90px;
  height: 90px;
  background: radial-gradient(circle, var(--hero-purple), transparent 70%);
}

.circle-purple:nth-child(1) { top: 20%; right: 10%; animation-delay: 0.5s; }
.circle-purple:nth-child(2) { bottom: 35%; left: 15%; animation-delay: 1.5s; width: 70px; height: 70px; }
.circle-purple:nth-child(3) { top: 50%; left: 30%; animation-delay: 2.5s; width: 50px; height: 50px; }

@keyframes floatShape {
  0%, 100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-20px) scale(1.05); }
}

.content-layer {
  z-index: 10;
}

.hero-content {
  text-align: center;
  color: white;
  max-width: 900px;
  padding: 0 2rem;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: rgba(251, 166, 54, 0.15);
  border: 1px solid var(--hero-gold-light);
  color: var(--hero-gold);
  padding: 0.6rem 1.8rem;
  border-radius: 50px;
  font-size: 0.8rem;
  font-weight: 700;
  margin-bottom: 2rem;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  backdrop-filter: blur(10px);
  animation: fadeInUp 1s ease forwards 0.3s;
  opacity: 0;
}

.hero-title {
  font-size: clamp(2.8rem, 6vw, 5rem);
  margin-bottom: 1.5rem;
  line-height: 1.05;
  text-shadow: 0 4px 30px rgba(0,0,0,0.3);
}

.hero-title .word {
  display: inline-block;
  animation: revealWord 0.8s ease forwards;
  opacity: 0;
  transform: rotateX(-45deg);
}

.hero-title .word:nth-child(odd) {
  color: white;
}

.hero-title .word:nth-child(even) {
  color: var(--hero-gold);
}

@keyframes revealWord {
  to {
    opacity: 1;
    transform: rotateX(0deg);
  }
}

.hero-subtitle {
  font-size: clamp(1.1rem, 2vw, 1.4rem);
  margin-bottom: 3rem;
  opacity: 0.9;
  max-width: 650px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.8;
  font-weight: 300;
  animation: fadeInUp 1s ease forwards 0.7s;
  opacity: 0;
}

.btn-group {
  display: flex;
  gap: 1.2rem;
  justify-content: center;
  flex-wrap: wrap;
  animation: fadeInUp 1s ease forwards 0.9s;
  opacity: 0;
}

.v-kids-position {
  position: absolute;
  top: 2rem;
  right: 2rem;
  z-index: 20;
}

.particles-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.ripple-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
  z-index: 100;
}

:deep(.ripple-effect) {
  position: absolute;
  width: 200px;
  height: 200px;
  margin-left: -100px;
  margin-top: -100px;
  border-radius: 50%;
  background: radial-gradient(circle, var(--hero-gold-light) 0%, transparent 70%);
  transform: scale(0);
  animation: rippleAnim 0.6s ease-out forwards;
}

@keyframes rippleAnim {
  to {
    transform: scale(4);
    opacity: 0;
  }
}

.scroll-indicator {
  position: absolute;
  bottom: 2.5rem;
  left: 50%;
  transform: translateX(-50%);
  color: white;
  animation: bounce 2.5s infinite;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.3s;
  z-index: 60;
}

.scroll-indicator:hover {
  opacity: 1;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes bounce {
  0%, 100% { transform: translateX(-50%) translateY(0); }
  50% { transform: translateX(-50%) translateY(-10px); }
}

@media (prefers-reduced-motion: reduce) {
  .parallax-wrapper,
  .shape,
  .hero-title .word,
  .hero-badge,
  .hero-subtitle,
  .btn-group,
  .scroll-indicator {
    animation: none !important;
    transform: none !important;
    opacity: 1 !important;
  }
  
  .hero-title .word {
    transform: none !important;
  }
}

@media (max-width: 768px) {
  .btn-group {
    flex-direction: column;
    align-items: center;
  }
  
  ion-button {
    width: 100%;
    max-width: 300px;
  }
  
  .v-kids-position {
    top: 1rem;
    right: 1rem;
  }
  
  .v-kids-position :deep(.v-kids-label) {
    display: none;
  }
}
</style>